import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Store, RefreshCw, AlertTriangle } from 'lucide-react';
import { StoreCard } from './StoreCard';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { extractStoresArray } from '@/lib/utils';
import {
  useGetApiWorkspacesWorkspaceIdStores,
  useDeleteApiWorkspacesWorkspaceIdStoresStoreId,
  getGetApiWorkspacesWorkspaceIdStoresQueryKey,
} from '@/api/generated/stores/stores';
import { toast } from 'sonner';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import type { StoreItem } from '@/types';

interface StoreGridProps {
  onOpenConnectModal: () => void;
}

export const StoreGrid: React.FC<StoreGridProps> = ({ onOpenConnectModal }) => {
  const queryClient = useQueryClient();
  const { activeWorkspaceId, currentRole } = useWorkspaceStore();
  const canManageStores = currentRole === 'Owner' || currentRole === 'Admin';

  const { data: stores, isLoading, isError, refetch } = useGetApiWorkspacesWorkspaceIdStores(activeWorkspaceId!, {
    query: { enabled: !!activeWorkspaceId }
  });
  const deleteStoreMutation = useDeleteApiWorkspacesWorkspaceIdStoresStoreId();

  const [storeToDisconnect, setStoreToDisconnect] = useState<StoreItem | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const handleDisconnectClick = (store: StoreItem) => {
    setStoreToDisconnect(store);
    setIsConfirmOpen(true);
  };

  const handleConfirmDisconnect = async () => {
    if (!storeToDisconnect || !activeWorkspaceId) return;
    try {
      await deleteStoreMutation.mutateAsync({ workspaceId: activeWorkspaceId, storeId: storeToDisconnect.id });
      toast.success(`Loja "${storeToDisconnect.name}" desconectada com sucesso`);
      queryClient.invalidateQueries({ queryKey: getGetApiWorkspacesWorkspaceIdStoresQueryKey(activeWorkspaceId) });
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Falha ao desconectar loja';
      toast.error(msg);
    } finally {
      setIsConfirmOpen(false);
      setStoreToDisconnect(null);
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-border/60 p-5 space-y-4 bg-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-xl" />
                <div className="space-y-1.5">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <div className="space-y-2 py-3 border-y border-border/40">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-3/4" />
            </div>
            <div className="flex gap-2">
              <Skeleton className="h-8 flex-1 rounded-md" />
              <Skeleton className="h-8 w-8 rounded-md" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-8 text-center space-y-3">
        <div className="h-12 w-12 rounded-full bg-destructive/10 text-destructive flex items-center justify-center mx-auto">
          <AlertTriangle className="h-6 w-6" />
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-foreground">Falha ao Carregar Integrações de Lojas</h3>
          <p className="text-xs text-muted-foreground max-w-sm mx-auto">
            Ocorreu um erro ao se comunicar com a API do backend. Por favor, tente novamente.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-1.5 text-xs">
          <RefreshCw className="h-3.5 w-3.5" />
          <span>Tentar Novamente</span>
        </Button>
      </div>
    );
  }

  const storesList = extractStoresArray(stores) as StoreItem[];

  if (storesList.length === 0) {
    return (
      <EmptyState
        icon={Store}
        title="Nenhuma Loja Virtual Conectada"
        description="Conecte sua primeira loja Shopify, Nuvemshop ou WooCommerce para ativar agentes autônomos de IA e bate-papo em tempo real."
        actionLabel={canManageStores ? "Conectar Sua Primeira Loja" : undefined}
        onAction={canManageStores ? onOpenConnectModal : undefined}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Lojas Conectadas ({storesList.length})
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="h-3 w-3" />
          <span>Atualizar</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {storesList.map((store) => (
          <StoreCard
            key={store.id}
            store={store}
            onDisconnect={handleDisconnectClick}
          />
        ))}
      </div>

      {/* Disconnect Confirmation Dialog */}
      <ConfirmDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        title="Desconectar Loja?"
        description={`Tem certeza de que deseja desconectar "${storeToDisconnect?.name}"? O atendimento automático por IA e as atualizações de clientes serão pausados para esta loja.`}
        confirmLabel="Desconectar Loja"
        cancelLabel="Manter Conectada"
        variant="destructive"
        onConfirm={handleConfirmDisconnect}
      />
    </div>
  );
};
